import { inject, Injectable } from '@angular/core';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Gender } from '@enums/gender';
import { TreatmentType } from '@models/treatment-types/treatment-type.model';
import { TreatmentTypeService } from '@services/treatment-type.service';

@Injectable({
  providedIn: 'root',
})
export class GenderService {
  private treatmentTypeService = inject(TreatmentTypeService);

  /**
   * Gets the invalid gender error message if the provided gender is disallowed for the current treatment type.
   *
   * @param {ConsultationTreatmentTypes} treatmentType The treatment type to check against.
   * @param {Gender} userGender The gender to check against the disallowed list.
   *
   * @returns {string | null} The invalid gender error message if disallowed, otherwise null.
   */
  public getGenderInvalidError(treatmentType: ConsultationTreatmentTypes, userGender: Gender): string | null {
    const treatmentTypeInstance = this.getTreatmentTypeInstance(treatmentType);

    if (!treatmentTypeInstance || !treatmentTypeInstance.isGenderDisallowed(userGender)) {
      return null;
    }

    return treatmentTypeInstance.genderSettings.errorMessage;
  }

  /**
   * Gets the gender information message for the provided gender and current treatment type.
   *
   * @param {ConsultationTreatmentTypes} treatmentType The treatment type to check against.
   * @param {Gender} userGender The gender to retrieve information message for.
   *
   * @returns {string} The gender information message.
   */
  public getGenderInfoMessage(treatmentType: ConsultationTreatmentTypes, userGender: Gender): string {
    return this.getTreatmentTypeInstance(treatmentType)?.getInfoMessageByGender(userGender) || '';
  }

  /**
   * Helper method to get the treatment type instance from storage service.
   *
   * @param {ConsultationTreatmentTypes} treatmentType The treatment type to retrieve.
   *
   * @returns {TreatmentType | null} The treatment type instance if found, otherwise null.
   */
  private getTreatmentTypeInstance(treatmentType: ConsultationTreatmentTypes): TreatmentType | null {
    if (!treatmentType) {
      return null;
    }

    return this.treatmentTypeService.getTreatmentType(treatmentType);
  }
}
